import { Button, ButtonGroup, Divider, DrawerBody, DrawerFooter, HStack, Text, useToast } from "@chakra-ui/react";
import { Drawer, Form } from "components";
import { API } from "config";
import { Mask } from "config";
import { useProduct, useProductCategories, useSuppliers } from "hooks";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router";

export default function Edit({ ...props }){

    const submitRef = useRef()
    const toast = useToast()
    const params = useParams()
    const product = useProduct(params.id)
    const categories = useProductCategories()
    const suppliers = useSuppliers()
    const [ form, setForm ] = useState({})
    const [ loading, setLoading ] = useState(false)
    const [ errors, setErrors ] = useState(false)

    const handleChange = ({ target }) => {
        const { name, value, checked } = target
        const _form = Object.assign({}, form)

        switch(name){
            case 'price': case 'cost': case 'points': _form[name] = Mask.price(value); break;
            case 'force_fixed_discount': case 'active': case 'public': case 'available_for_bonihub': _form[name] = checked; break;
            case 'height': case 'width': case 'length': _form['specifications'] = { ...form?.specifications, [name]: value }; break;
            case 'weight': _form['specifications'] = { ...form?.specifications, [name]: Mask.price(value) }; break;
            case 'min_stock': _form[name] = value === '' ? null : parseInt(value, 10); break;
            default: _form[name] = value; break;
        }

        setForm(_form)
    }

    const handleSubmit = async () => {
        if(!window.confirm('Tem certeza que deseja alterar os dados do produto?')) return
        setLoading(true)

        try{
            await API.put(`/products/${product.data.id}`, form)
            toast({ title: 'Dados do produto atualizados', status :'success' })
            product.refetch()
            props.onClose()
        }catch(err){
            setErrors(err?.response?.data?.errors)
            toast({ title: 'Não foi possível atualizar', description: err?.response?.data?.message, status: 'warning' })
        }

        setLoading(false)
    }

    return(
    <>
    <Drawer title={`Editar ${product.data?.name}`} size="md" {...props}>

        <DrawerBody>

            <Form.Form submitRef={submitRef} onSubmit={handleSubmit}>

                <Form.Text
                    required
                    label="Nome:"
                    name="name"
                    value={form?.name}
                    error={errors?.name}
                    onChange={handleChange}
                    defaultValue={product.data?.name}
                    isDisabled={loading}
                    size="lg"
                />

                <Form.Text
                    label="Tagline:"
                    name="tagline"
                    value={form?.tagline}
                    error={errors?.tagline}
                    onChange={handleChange}
                    defaultValue={product.data?.tagline}
                    isDisabled={loading}
                    helper="Opcional. Uma frase curta de até 140 caracteres para descrever o produto"
                />

                <Form.Textarea
                    label="Descrição:"
                    name="body"
                    value={form?.body}
                    error={errors?.body}
                    onChange={handleChange}
                    defaultValue={product.data?.body}
                    isDisabled={loading}
                    helper="Descrição completa do produto"
                />

                <Form.Select
                    required
                    label="Categoria:"
                    name="category_id"
                    onChange={handleChange}
                    options={categories.data?.map(item => ({ label: item.name, value: item.id }))}
                    value={form?.category_id || product.data?.category_id}
                    isDisabled={loading}
                />

                <Form.Select
                    required
                    label="Subcategoria:"
                    name="subcategory_id"
                    onChange={handleChange}
                    options={categories.data?.map(item => ({ label: item.name, value: item.id }))}
                    value={form?.subcategory_id || product.data?.subcategory_id}
                    isDisabled={loading}
                />

                <Form.Select
                    required
                    label="Fornecedor:"
                    name="supplier_id"
                    onChange={handleChange}
                    options={suppliers.data?.map(item => ({ label: item.name, value: item.id }))}
                    value={form?.supplier_id || product.data?.supplier_id}
                    isDisabled={loading}
                />

                <Divider />
                <Text children="Status do produto:" fontSize="xs" fontWeight="bold" color="GrayText" />

                <HStack>
                    <Form.Switch
                        label="Se o produto está ativo:"
                        name="active"
                        isChecked={form?.active}
                        defaultChecked={product.data?.active}
                        error={errors?.active}
                        isDisabled={loading}
                        onChange={handleChange}
                    />

                    <Form.Switch
                        label="Se o produto é publico:"
                        name="public"
                        isChecked={form?.public}
                        defaultChecked={product.data?.public}
                        error={errors?.public}
                        isDisabled={loading}
                        onChange={handleChange}
                    />
                </HStack>

                <Divider />
                <Text children="Informações monetárias:" fontSize="xs" fontWeight="bold" color="GrayText" />

                <Form.Text
                    required
                    label="Valor:"
                    name="price"
                    value={form?.price}
                    error={errors?.price}
                    onChange={handleChange}
                    defaultValue={Mask.price(product.data?.price)}
                    isDisabled={loading}
                    leftAddon="R$"
                    fontWeight="bold"
                    fontSize="1.3rem"
                    helper="O valor de venda final do produto"
                />

                <Form.Text
                    label="Custo:"
                    name="cost"
                    value={form?.cost}
                    error={errors?.cost}
                    onChange={handleChange}
                    defaultValue={Mask.price(product.data?.cost)}
                    isDisabled={loading}
                    leftAddon="R$"
                    helper="O valor de custo do produto"
                />

                <HStack>
                    <Form.Text
                        required
                        type="number"
                        min="0"
                        label="Desconto para consultores (%):"
                        name="wholesale_discount"
                        value={form?.wholesale_discount}
                        error={errors?.wholesale_discount}
                        onChange={handleChange}
                        defaultValue={product.data?.wholesale_discount}
                        isDisabled={loading}
                        rightAddon="%"
                        helper="A porcentagem de desconto de venda para os consultores"
                    />

                    <Form.Text
                        required
                        type="number"
                        min="0"
                        label="Desconto para núcleos (%):"
                        name="distributor_discount"
                        value={form?.distributor_discount}
                        error={errors?.distributor_discount}
                        onChange={handleChange}
                        defaultValue={product.data?.distributor_discount}
                        isDisabled={loading}
                        rightAddon="%"
                        helper="A porcentagem de desconto de venda para os núcleos"
                    />

                </HStack>
                <HStack>

                    <Form.Text
                        required
                        type="number"
                        min="0"
                        label="Desconto adicional mínimo para núcleos (%):"
                        name="min_discount"
                        value={form?.min_discount}
                        error={errors?.min_discount}
                        onChange={handleChange}
                        defaultValue={product.data?.min_discount}
                        isDisabled={loading}
                        rightAddon="%"
                        helper="A porcentagem de desconto mínima para 25.000,00 de compras mensais no mês anterior"
                    />

                    <Form.Text
                        required
                        type="number"
                        min="0"
                        label="Desconto adicional máximo para núcleos (%):"
                        name="max_discount"
                        value={form?.max_discount}
                        error={errors?.max_discount}
                        onChange={handleChange}
                        defaultValue={product.data?.max_discount}
                        isDisabled={loading}
                        rightAddon="%"
                        helper="A porcentagem de desconto máxima para 50.000,00 de compras mensais no mês anterior"
                    />

                    <Form.Switch
                        label="Se sempre fica o desconto padrão para núcleo:"
                        name="force_fixed_discount"
                        isChecked={form?.force_fixed_discount}
                        defaultChecked={product.data?.force_fixed_discount}
                        error={errors?.force_fixed_discount}
                        isDisabled={loading}
                        onChange={handleChange}
                    />

                </HStack>

                <Divider />
                <Text children="Limites:" fontSize="xs" fontWeight="bold" color="GrayText" />

                <HStack>
                    <Form.Text
                        required
                        type="number"
                        min="0"
                        label="Compra mínima:"
                        name="wholesale_min"
                        value={form?.wholesale_min}
                        error={errors?.wholesale_min}
                        onChange={handleChange}
                        defaultValue={product.data?.wholesale_min}
                        isDisabled={loading}
                        helper="O mínimo de itens por pedido de consultor"
                    />

                    <Form.Text
                        required
                        type="number"
                        min="0"
                        label="Compra máxima:"
                        name="wholesale_max"
                        value={form?.wholesale_max}
                        error={errors?.wholesale_max}
                        onChange={handleChange}
                        defaultValue={product.data?.wholesale_max}
                        isDisabled={loading}
                        helper="O máximo de itens por pedido de consultor"
                    />
                </HStack>

                <Divider />
                <Text children="Configurações de rede:" fontSize="xs" fontWeight="bold" color="GrayText" />

                <Form.Text
                        required
                        label="Pontos gerados:"
                        name="points"
                        value={form?.points}
                        error={errors?.points}
                        onChange={handleChange}
                        defaultValue={Mask.price(product.data?.points)}
                        isDisabled={loading}
                        rightAddon="pontos"
                        helper="Quantidade de pontos gerados por item adquirido"
                    />

                <Divider />
                <Text children="Bling:" fontSize="xs" fontWeight="bold" color="GrayText" />

                <Form.Text
                    label="Bling ID:"
                    name="tagline"
                    value={form?.bling_id}
                    error={errors?.bling_id}
                    onChange={handleChange}
                    defaultValue={product.data?.bling_id}
                    isDisabled={loading}
                    helper="O ID do produto cadastrado no Bling"
                />

                <Divider />
                <Text children="Controle de Estoque:" fontSize="xs" fontWeight="bold" color="GrayText" />

                <Form.Text
                    type="number"
                    min="0"
                    label="Estoque mínimo:"
                    name="min_stock"
                    value={form?.min_stock}
                    error={errors?.min_stock}
                    onChange={handleChange}
                    defaultValue={product.data?.min_stock ?? ''}
                    isDisabled={loading}
                    rightAddon="un."
                    helper="Alerta no painel quando o estoque total ficar abaixo deste valor. Deixe em branco para desativar."
                />

                <Divider />
                <Text children="Bonihub:" fontSize="xs" fontWeight="bold" color="GrayText" />

                <Form.Switch
                    label="Disponível no Bonihub:"
                    name="available_for_bonihub"
                    isChecked={form?.available_for_bonihub}
                    defaultChecked={product.data?.available_for_bonihub}
                    error={errors?.available_for_bonihub}
                    isDisabled={loading}
                    onChange={handleChange}
                />

                <Divider />
                <Text children="Especificações:" fontSize="xs" fontWeight="bold" color="GrayText" />

                <Form.Text
                    required
                    type="number"
                    min="0"
                    label="Altura do produto:"
                    name="height"
                    value={form?.specifications?.height}
                    error={errors?.specifications?.height}
                    onChange={handleChange}
                    defaultValue={product.data?.specifications?.height ?? 0}
                    isDisabled={loading}
                    rightAddon="cm"
                />

                <Form.Text
                    required
                    type="number"
                    min="0"
                    label="Largura do produto:"
                    name="width"
                    value={form?.specifications?.width}
                    error={errors?.specifications?.width}
                    onChange={handleChange}
                    defaultValue={product.data?.specifications?.width ?? 0}
                    isDisabled={loading}
                    rightAddon="cm"
                />

                <Form.Text
                    required
                    type="number"
                    min="0"
                    label="Comprimento do produto:"
                    name="length"
                    value={form?.specifications?.length}
                    error={errors?.specifications?.length}
                    onChange={handleChange}
                    defaultValue={product.data?.specifications?.length ?? 0}
                    isDisabled={loading}
                    rightAddon="cm"
                />

                <Form.Text
                    required
                    label="Peso do produto:"
                    name="weight"
                    value={form?.specifications?.weight}
                    error={errors?.specifications?.weight}
                    onChange={handleChange}
                    defaultValue={Mask.price(parseFloat(product.data?.specifications?.weight).toFixed(2))}
                    isDisabled={loading}
                    rightAddon="kg"
                />

            </Form.Form>

        </DrawerBody>

        <DrawerFooter>
            <ButtonGroup isDisabled={loading}>
                <Button children="Atualizar dados" isLoading={loading} onClick={() => submitRef.current.click()} />
                <Button children="Cancelar" colorScheme="red" onClick={props.onClose} />
            </ButtonGroup>
        </DrawerFooter>

    </Drawer>
    </>
    )
}
